/**
 * 0.39.63 - resolveTitleText
 *
 * Resolves a page/form NAME (or title) string that may be a
 * `=lib(...)` expression into display text, for the surfaces that show
 * the name outside the form renderer: the public header nav, footer
 * links, the browser tab title, the StaticPage heading, the admin
 * Pages-and-forms table, and the form editor's read-only page title.
 *
 * A plain string is returned as-is. A `=lib("libkey","keycol",
 * "keyval","valcol")` expression is evaluated using the shared
 * expression engine + the library cache (same path resolveAdminText
 * uses inside the form renderer). Library values are per-language, so
 * pass the active language.
 *
 * lookupLibraryValue serves cached values synchronously and triggers a
 * background fetch on a miss (notifying subscribers). Components that
 * want to re-render when the value hydrates should subscribe to the
 * library cache (see useResolvedTitle).
 */
import { isExpression, extractExpression, evaluateExpression } from './rules/expression';
import { lookupLibraryValue, subscribeLibraryCache } from './libraryCache';
import { useEffect, useState } from 'react';

export function resolveTitleText(raw, language) {
  if (raw == null) return '';
  if (typeof raw !== 'string') return String(raw);
  if (!isExpression(raw)) return raw;
  try {
    const functions = {
      lib: (libKey, keyColumn, keyValue, valueColumn) =>
        lookupLibraryValue(libKey, keyColumn, keyValue, valueColumn, language),
    };
    const evaluated = evaluateExpression(extractExpression(raw), {}, functions);
    if (evaluated === undefined || evaluated === null) return '';
    return String(evaluated);
  } catch (_e) {
    // 0.43.74 - Never show a formula to a citizen: a title whose
    // formula cannot be evaluated here shows nothing (was: the raw
    // "=..." text).
    return '';
  }
}

/**
 * React hook: resolve a =lib() title and re-render when the library
 * cache hydrates (so an initially-empty lib() value fills in without
 * a manual refresh). For a plain string it returns it directly.
 */
export function useResolvedTitle(raw, language) {
  const [resolved, setResolved] = useState(() => resolveTitleText(raw, language));
  useEffect(() => {
    setResolved(resolveTitleText(raw, language));
    if (typeof raw === 'string' && isExpression(raw)) {
      // Re-resolve when any library data arrives.
      const unsub = subscribeLibraryCache(() => {
        setResolved(resolveTitleText(raw, language));
      });
      return unsub;
    }
    return undefined;
  }, [raw, language]);
  return resolved;
}

/**
 * 0.45.55 - Re-render the calling component whenever library data
 * arrives. For components that call resolveTitleText() for several
 * labels (the header's navigation menu): a =lib(...) label is empty
 * until its row is fetched, and without this the menu stayed empty
 * until the page was reloaded, on the first visit and after every
 * language change.
 */
export function useLibraryCacheRefresh() {
  const [, setTick] = useState(0);
  useEffect(() => subscribeLibraryCache(() => setTick((n) => n + 1)), []);
}
